import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const apiClient = axios.create({ baseURL: API });

// attach Bearer token from localStorage automatically
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("tc_auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Public ----
export const listProducts = (opts) => {
  // Back-compat: legacy callers pass a bare category string.
  const params = typeof opts === "string" || opts == null
    ? (opts ? { category: opts } : {})
    : {
        ...(opts.category ? { category: opts.category } : {}),
        ...(opts.q ? { q: opts.q } : {}),
      };
  return apiClient.get("/products", { params }).then((r) => r.data);
};
export const getProduct = (id) => apiClient.get(`/products/${id}`).then((r) => r.data);

export const listReadyMade = (params = {}) =>
  apiClient.get("/ready-made", { params }).then((r) => r.data);
export const getReadyMade = (id) => apiClient.get(`/ready-made/${id}`).then((r) => r.data);

// ---- Blog ----
export const listBlogPosts = (tag) =>
  apiClient.get("/blog", { params: tag ? { tag } : {} }).then((r) => r.data);
export const getBlogPost = (slug) => apiClient.get(`/blog/${slug}`).then((r) => r.data);

export const listClipart = () => apiClient.get("/clipart").then((r) => r.data);
export const getPickupAddress = () => apiClient.get("/pickup-address").then((r) => r.data);
export const trustStats = () => apiClient.get("/trust/stats").then((r) => r.data);

export const getCartQuote = (body) => apiClient.post("/cart/quote", body).then((r) => r.data);
export const validateDiscount = (code, subtotal) =>
  apiClient.post("/discount/validate", { code, subtotal }).then((r) => r.data);

export const createCheckoutSession = (body) =>
  apiClient.post("/checkout/session", body).then((r) => r.data);
export const checkoutConfig = () =>
  apiClient.get("/checkout/config").then((r) => r.data);
export const checkoutPay = (body) =>
  apiClient.post("/checkout/pay", body, { timeout: 30000 }).then((r) => r.data);
export const getCheckoutStatus = (sid) =>
  apiClient.get(`/checkout/status/${sid}`).then((r) => r.data);
export const getOrderBySession = (sid) =>
  apiClient.get(`/orders/by-session/${sid}`).then((r) => r.data);

export const submitQuoteRequest = (body) =>
  apiClient.post("/quote-requests", body).then((r) => r.data);

export const uploadImage = (data_url, name) =>
  apiClient.post("/uploads", { data_url, name }).then((r) => r.data);

// iter108 — batch upload used by the customizer at add-to-cart time so
// mockups + uploaded artwork + image-layer contents get a persistent
// upload_id BEFORE the cart is ever server-persisted. Cart restore then
// rehydrates the raw data URLs from these ids on the server side.
export const uploadImagesBatch = (items) =>
  apiClient.post("/uploads/batch", { items }).then((r) => r.data);

export const rehydrateUploadIds = (ids) =>
  apiClient.post("/uploads/rehydrate", { ids }).then((r) => r.data);

export const trackOrder = (order_number, email) =>
  apiClient.post("/orders/track", { order_number, email }).then((r) => r.data);

// ---- Auth ----
export const authSignup = (body) => apiClient.post("/auth/signup", body).then((r) => r.data);
export const authLogin = (body) => apiClient.post("/auth/login", body).then((r) => r.data);
export const authMe = () => apiClient.get("/auth/me").then((r) => r.data);
// iter82 — Emergent-managed Google Auth. Frontend hits this after
// receiving the session_id fragment from auth.emergentagent.com.
export const authGoogleCallback = (sessionId) =>
  apiClient.post("/auth/google/callback", null, {
    headers: { "X-Session-ID": sessionId },
  }).then((r) => r.data);
export const passwordResetRequest = (email) =>
  apiClient.post("/auth/password-reset-request", { email }).then((r) => r.data);
export const passwordReset = (token, password) =>
  apiClient.post("/auth/password-reset", { token, password }).then((r) => r.data);
export const myOrders = () => apiClient.get("/me/orders").then((r) => r.data);

// Saved Designs
export const listMyDesigns = () => apiClient.get("/me/designs").then((r) => r.data);
export const saveMyDesign = (body) => apiClient.post("/me/designs", body).then((r) => r.data);
export const updateMyDesign = (id, body) => apiClient.patch(`/me/designs/${id}`, body).then((r) => r.data);
export const deleteMyDesign = (id) => apiClient.delete(`/me/designs/${id}`).then((r) => r.data);
export const getSharedDesign = (share_id) => apiClient.get(`/designs/shared/${share_id}`).then((r) => r.data);

// ---- Admin ----
const adminHeaders = (pw) => ({ headers: { "X-Admin-Password": pw } });
export const adminLogin = (password) => apiClient.post("/admin/login", { password }).then((r) => r.data);
export const adminStats = (pw) => apiClient.get("/admin/stats", adminHeaders(pw)).then((r) => r.data);
export const adminListOrders = (pw) => apiClient.get("/admin/orders", adminHeaders(pw)).then((r) => r.data);
export const adminGetOrder = (id, pw) => apiClient.get(`/admin/orders/${id}`, adminHeaders(pw)).then((r) => r.data);
export const adminScanMockups = (pw) => apiClient.get("/admin/mockups/scan", adminHeaders(pw)).then((r) => r.data);
export const adminRepairMockups = (pw, order_number) =>
  apiClient.post("/admin/mockups/repair", order_number ? { order_number } : {}, adminHeaders(pw)).then((r) => r.data);
export const adminUpdateOrder = (id, body, pw) =>
  apiClient.patch(`/admin/orders/${id}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminListProducts = (pw) =>
  apiClient.get("/products", { params: { include_inactive: true }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminCreateProduct = (body, pw) =>
  apiClient.post("/admin/products", body, adminHeaders(pw)).then((r) => r.data);
export const adminUpdateProduct = (id, body, pw) =>
  apiClient.patch(`/admin/products/${id}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteProduct = (id, pw) =>
  apiClient.delete(`/admin/products/${id}`, adminHeaders(pw)).then((r) => r.data);
export const adminListReadyMade = (pw) =>
  apiClient.get("/ready-made", { params: { include_inactive: true }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminListAbandonedCarts = (pw, thresholdMinutes = 30) =>
  apiClient.get(`/admin/abandoned-carts`, { params: { threshold_minutes: thresholdMinutes }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminSendCartRecovery = (email, pw) =>
  apiClient.post(`/admin/abandoned-carts/${encodeURIComponent(email)}/send-recovery`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminResendOrderConfirmation = (orderId, pw) =>
  apiClient.post(`/admin/orders/${orderId}/resend-confirmation`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminForceConfirmOrder = (orderId, pw, reason) =>
  apiClient.post(`/admin/orders/${orderId}/force-confirm`, { reason }, adminHeaders(pw)).then((r) => r.data);
export const adminNudgePendingOrder = (orderId, pw, code) =>
  apiClient.post(`/admin/orders/${orderId}/nudge`,
    code ? { code } : {}, adminHeaders(pw)).then((r) => r.data);
export const adminTestWebhook = (pw) =>
  apiClient.post("/admin/test-webhook", {}, adminHeaders(pw)).then((r) => r.data);
export const adminRevenueSeries = (pw, days = 30) =>
  apiClient.get(`/admin/revenue-timeseries`, { params: { days }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminWebhookDeliveries = (pw, limit = 20) =>
  apiClient.get(`/admin/webhook-deliveries`, { params: { limit }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminAltTextAudit = (pw) =>
  apiClient.get(`/admin/alt-text-audit`, adminHeaders(pw)).then((r) => r.data);
export const adminSetOrderNotes = (orderId, internal_notes, pw) =>
  apiClient.patch(`/admin/orders/${orderId}/notes`, { internal_notes }, adminHeaders(pw)).then((r) => r.data);
export const adminSetQuoteNotes = (qid, internal_notes, pw) =>
  apiClient.patch(`/admin/quote-requests/${qid}/notes`, { internal_notes }, adminHeaders(pw)).then((r) => r.data);
export const adminAuditLog = (pw, limit = 100) =>
  apiClient.get(`/admin/audit-log`, { params: { limit }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminCustomerProfile = (email, pw) =>
  apiClient.get(`/admin/customer-profile`, { params: { email }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminRefundOrder = (orderId, payload, pw) =>
  apiClient.post(`/admin/orders/${orderId}/refund`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminSetStockStatus = (rmId, stock_status, pw) =>
  apiClient.patch(`/admin/ready-made/${rmId}/stock-status`, { stock_status }, adminHeaders(pw)).then((r) => r.data);
export const adminMigrateColours = (pw) =>
  apiClient.post(`/admin/ready-made/migrate-colours`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminNormalizeReadyMadeColours = (pw) =>
  apiClient.post(`/admin/ready-made/normalize-colours`, {}, adminHeaders(pw)).then((r) => r.data);
// Iter77 — Etsy-style bulk management endpoints
export const adminProductsBulkUpdate = (payload, pw) =>
  apiClient.post(`/admin/products/bulk-update`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminProductsBulkDelete = (payload, pw) =>
  apiClient.post(`/admin/products/bulk-delete`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminDuplicateProduct = (id, pw) =>
  apiClient.post(`/admin/products/${id}/duplicate`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminReadyMadeBulkUpdate = (payload, pw) =>
  apiClient.post(`/admin/ready-made/bulk-update`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminReadyMadeBulkDelete = (payload, pw) =>
  apiClient.post(`/admin/ready-made/bulk-delete`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminDuplicateReadyMade = (id, pw) =>
  apiClient.post(`/admin/ready-made/${id}/duplicate`, {}, adminHeaders(pw)).then((r) => r.data);
// iter78 — drag-to-reorder endpoints + public bestsellers
export const adminProductsReorder = (ids, pw) =>
  apiClient.post(`/admin/products/reorder`, { ids }, adminHeaders(pw)).then((r) => r.data);
export const adminReadyMadeReorder = (ids, pw) =>
  apiClient.post(`/admin/ready-made/reorder`, { ids }, adminHeaders(pw)).then((r) => r.data);
export const listBestsellers = () =>
  apiClient.get(`/bestsellers`).then((r) => r.data);

// iter79 — S&S Activewear (Canada) sync
export const ssStatus = (pw) => apiClient.get(`/admin/ss/status`, adminHeaders(pw)).then((r) => r.data);
export const ssSync = (payload, pw) =>
  apiClient.post(`/admin/ss/sync`, payload || {}, adminHeaders(pw)).then((r) => r.data);
export const ssRetryFailed = (pw) =>
  apiClient.post(`/admin/ss/retry-failed`, {}, adminHeaders(pw)).then((r) => r.data);
export const ssApproveAll = (pw) =>
  apiClient.post(`/admin/ss/approve-all`, {}, adminHeaders(pw)).then((r) => r.data);
export const ssStaged = (params, pw) =>
  apiClient.get(`/admin/ss/staged`, { ...adminHeaders(pw), params }).then((r) => r.data);
export const ssSetApproval = (styleId, approved, pw) =>
  apiClient.put(`/admin/ss/staged/${styleId}/approval`, { approved }, adminHeaders(pw)).then((r) => r.data);
export const ssPromote = (payload, pw) =>
  apiClient.post(`/admin/ss/promote`, payload || {}, adminHeaders(pw)).then((r) => r.data);
export const ssReset = (pw) =>
  apiClient.post(`/admin/ss/reset`, { confirm: "DELETE" }, adminHeaders(pw)).then((r) => r.data);
export const adminOrderPrinterPdfUrl = (orderId) => `${API}/admin/orders/${orderId}/printer-pdf`;
export const adminOrderInvoicePdfUrl = (orderId) => `${API}/admin/orders/${orderId}/invoice.pdf`;
export const adminOrderFiles = (orderId, pw) =>
  apiClient.get(`/admin/orders/${orderId}/files`, adminHeaders(pw)).then((r) => r.data);
export const adminEmailInvoice = (orderId, payload, pw) =>
  apiClient.post(`/admin/orders/${orderId}/email-invoice`, payload || {}, adminHeaders(pw)).then((r) => r.data);
export const adminMarkShipped = (orderId, payload, pw) =>
  apiClient.post(`/admin/orders/${orderId}/mark-shipped`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminSetFulfillment = (orderId, fulfillment_status, pw, sendEmail = true) =>
  apiClient
    .patch(
      `/admin/orders/${orderId}/fulfillment`,
      { fulfillment_status, send_email: sendEmail },
      adminHeaders(pw),
    )
    .then((r) => r.data);
export const adminNotificationSummary = (pw, since) =>
  apiClient.get(`/admin/notifications/summary`, { params: since ? { since } : {}, ...adminHeaders(pw) }).then((r) => r.data);

// AI background remover — free, runs on /api/ai/remove-bg.
// Used by both the DTF gang sheet builder and the garment customizer
// (replacing the legacy $10/file manual service).
export const aiRemoveBg = (dataUrl) =>
  apiClient.post("/ai/remove-bg", { image: dataUrl }, { timeout: 120000 }).then((r) => r.data);
export const adminTrafficStats = (pw, days = 30) =>
  apiClient.get(`/admin/traffic/stats`, { params: { days }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminTrafficRecent = (pw, limit = 100) =>
  apiClient.get(`/admin/traffic/recent`, { params: { limit }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminSetProductionDueDate = (orderId, production_due_date, pw) =>
  apiClient.patch(
    `/admin/orders/${orderId}/production-due-date`,
    { production_due_date },
    adminHeaders(pw),
  ).then((r) => r.data);
export const adminProductionCalendar = (pw, month) =>
  apiClient.get(`/admin/production/calendar`, {
    params: month ? { month } : {},
    ...adminHeaders(pw),
  }).then((r) => r.data);

// ===== SHARED CART =====
export const createSharedCart = (payload, pw) => {
  // If the admin is logged in we forward the password so the backend
  // marks the link as "created_by_admin" and writes an audit log.
  const cfg = pw ? adminHeaders(pw) : {};
  return apiClient.post(`/shared-cart`, payload, cfg).then((r) => r.data);
};
export const getSharedCart = (token) =>
  apiClient.get(`/shared-cart/${encodeURIComponent(token)}`).then((r) => r.data);
export const adminListSharedCarts = (pw) =>
  apiClient.get(`/admin/shared-carts`, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteSharedCart = (id, pw) =>
  apiClient.delete(`/admin/shared-carts/${id}`, adminHeaders(pw)).then((r) => r.data);
export const adminEmailSharedCart = (id, body, pw) =>
  apiClient.post(`/admin/shared-carts/${id}/email`, body || {}, adminHeaders(pw)).then((r) => r.data);

// ===== SEGMENTS & EMAIL BLASTS =====
export const adminListSegments = (pw) =>
  apiClient.get(`/admin/segments`, adminHeaders(pw)).then((r) => r.data);
export const adminPreviewSegment = (key, pw, limit = 25) =>
  apiClient.get(`/admin/segments/${key}/preview`, { params: { limit }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminSendBlast = (payload, pw) =>
  apiClient.post(`/admin/segments/email`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminScheduleBlast = (payload, pw) =>
  apiClient.post(`/admin/blasts/schedule`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminListBlasts = (pw) =>
  apiClient.get(`/admin/blasts`, adminHeaders(pw)).then((r) => r.data);
export const adminCancelBlast = (id, pw) =>
  apiClient.delete(`/admin/blasts/${id}`, adminHeaders(pw)).then((r) => r.data);

// ===== EMAIL CALENDAR =====
export const adminEmailCalendar = (pw) =>
  apiClient.get(`/admin/email-calendar`, adminHeaders(pw)).then((r) => r.data);
export const adminPauseCustomerEmails = (payload, pw) =>
  apiClient.post(`/admin/email-calendar/pause`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminListOptOuts = (pw) =>
  apiClient.get(`/admin/email-calendar/opt-outs`, adminHeaders(pw)).then((r) => r.data);
export const adminEmailCalendarPreview = (stream, email, pw) =>
  apiClient.get(`/admin/email-calendar/preview`, { params: { stream, email }, ...adminHeaders(pw) }).then((r) => r.data);

// ===== SAVED BLAST TEMPLATES =====
export const adminListBlastTemplates = (pw) =>
  apiClient.get(`/admin/blast-templates`, adminHeaders(pw)).then((r) => r.data);
export const adminCreateBlastTemplate = (payload, pw) =>
  apiClient.post(`/admin/blast-templates`, payload, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteBlastTemplate = (id, pw) =>
  apiClient.delete(`/admin/blast-templates/${id}`, adminHeaders(pw)).then((r) => r.data);

// ===== LOW STOCK =====
export const adminLowStock = (pw, threshold = 10) =>
  apiClient.get(`/admin/low-stock`, { params: { threshold }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminSendLowStockDigest = (pw, threshold = 10) =>
  apiClient.post(`/admin/low-stock/notify`, null, { params: { threshold }, ...adminHeaders(pw) }).then((r) => r.data);

// ===== PROFIT MARGINS =====
export const adminMarginSummary = (pw, days = 30, with_orders = false) =>
  apiClient.get(`/admin/margins/summary`, { params: { days, with_orders }, ...adminHeaders(pw) }).then((r) => r.data);
export const adminSetProductCost = (productId, cost_per_unit, pw) =>
  apiClient.patch(`/admin/products/${productId}/cost`, { cost_per_unit }, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteOrder = (orderId, pw) =>
  apiClient.delete(`/admin/orders/${orderId}`, adminHeaders(pw)).then((r) => r.data);
export const adminListNewsletterSubscribers = (pw) =>
  apiClient.get("/admin/newsletter-subscribers", adminHeaders(pw)).then((r) => r.data);
export const adminNewsletterCsvUrl = () =>
  `${apiClient.defaults.baseURL}/admin/newsletter-subscribers?fmt=csv`;
export const adminCreateReadyMade = (body, pw) =>
  apiClient.post("/admin/ready-made", body, adminHeaders(pw)).then((r) => r.data);
export const adminUpdateReadyMade = (id, body, pw) =>
  apiClient.patch(`/admin/ready-made/${id}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteReadyMade = (id, pw) =>
  apiClient.delete(`/admin/ready-made/${id}`, adminHeaders(pw)).then((r) => r.data);
export const adminListDiscounts = (pw) =>
  apiClient.get("/admin/discounts", adminHeaders(pw)).then((r) => r.data);
export const adminCreateDiscount = (body, pw) =>
  apiClient.post("/admin/discounts", body, adminHeaders(pw)).then((r) => r.data);
export const adminUpdateDiscount = (id, body, pw) =>
  apiClient.patch(`/admin/discounts/${id}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteDiscount = (id, pw) =>
  apiClient.delete(`/admin/discounts/${id}`, adminHeaders(pw)).then((r) => r.data);
export const adminListQuotes = (pw) =>
  apiClient.get("/admin/quote-requests", adminHeaders(pw)).then((r) => r.data);
export const adminUpdateQuote = (id, body, pw) =>
  apiClient.patch(`/admin/quote-requests/${id}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteQuote = (id, pw) =>
  apiClient.delete(`/admin/quote-requests/${id}`, adminHeaders(pw)).then((r) => r.data);

// ===== SERVER-SIDE CART PERSISTENCE =====
// Saves the cart in MongoDB keyed by anon_id (browser cookie) and
// optionally user_id (Bearer JWT) so a returning visitor can pick up
// where they left off — even across devices once logged in.
export const persistCart = (anon_id, items, subtotal_estimate) =>
  apiClient.post(
    "/cart/persist",
    { anon_id, items, subtotal_estimate },
    // 8s timeout — this is a background sync, not user-blocking.
    { timeout: 8000 },
  ).then((r) => r.data);
export const restoreCart = (anon_id) =>
  apiClient.get("/cart/restore", { params: { anon_id }, timeout: 8000 }).then((r) => r.data);
export const clearPersistedCart = (anon_id) =>
  apiClient.delete("/cart/persist", { params: { anon_id }, timeout: 8000 }).then((r) => r.data);

// ===== EMAIL HEALTH (Admin) =====
export const adminEmailHealth = (pw, windowHours = 24) =>
  apiClient.get("/admin/email/health", { ...adminHeaders(pw), params: { window_hours: windowHours } }).then((r) => r.data);
export const adminEmailRecent = (pw, limit = 50) =>
  apiClient.get("/admin/email/recent", { ...adminHeaders(pw), params: { limit } }).then((r) => r.data);
export const adminEmailTest = (pw, to) =>
  apiClient.post("/admin/email/test", { to }, adminHeaders(pw)).then((r) => r.data);

// ===== TIDIO LIVE CHAT (replaces the old LinkedPhone/Zapier custom chat) =====
export const getTidioPublic = () => apiClient.get("/site/tidio", { timeout: 8000 }).then((r) => r.data);
export const adminGetTidio = (pw) =>
  apiClient.get("/admin/site/tidio", adminHeaders(pw)).then((r) => r.data);
export const adminPutTidio = (pw, body) =>
  apiClient.put("/admin/site/tidio", body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteTidio = (pw) =>
  apiClient.delete("/admin/site/tidio", adminHeaders(pw)).then((r) => r.data);

// ---- Ready-Made global sale (iter73) ----
export const getReadyMadeSalePublic = () =>
  apiClient.get("/site/sale", { timeout: 8000 }).then((r) => r.data);
export const adminGetReadyMadeSale = (pw) =>
  apiClient.get("/admin/site/sale", adminHeaders(pw)).then((r) => r.data);
export const adminPutReadyMadeSale = (pw, body) =>
  apiClient.put("/admin/site/sale", body, adminHeaders(pw)).then((r) => r.data);

// ---- Meta Pixel settings (iter47t) ----
export const getMetaPixelPublic = () =>
  apiClient.get("/site/meta-pixel", { timeout: 8000 }).then((r) => r.data);
export const adminGetMetaPixel = (pw) =>
  apiClient.get("/admin/site/meta-pixel", adminHeaders(pw)).then((r) => r.data);
export const adminPutMetaPixel = (pw, body) =>
  apiClient.put("/admin/site/meta-pixel", body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteMetaPixel = (pw) =>
  apiClient.delete("/admin/site/meta-pixel", adminHeaders(pw)).then((r) => r.data);

// ---- Google Ads (conversion tracking config + AI ad copy) ----
export const getGoogleAdsPublic = () =>
  apiClient.get("/site/google-ads", { timeout: 8000 }).then((r) => r.data);
export const adminGetGoogleAds = (pw) =>
  apiClient.get("/admin/site/google-ads", adminHeaders(pw)).then((r) => r.data);
export const adminPutGoogleAds = (pw, body) =>
  apiClient.put("/admin/site/google-ads", body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteGoogleAds = (pw) =>
  apiClient.delete("/admin/site/google-ads", adminHeaders(pw)).then((r) => r.data);
export const adminGenerateAdCopy = (pw, body) =>
  apiClient.post("/admin/ads/generate-copy", body, { ...adminHeaders(pw), timeout: 60000 }).then((r) => r.data);

// ---- Google Local Feed settings (iter47w) ----
export const adminGetGoogleLocalFeed = (pw) =>
  apiClient.get("/admin/site/google-local-feed", adminHeaders(pw)).then((r) => r.data);
export const adminPutGoogleLocalFeed = (pw, body) =>
  apiClient.put("/admin/site/google-local-feed", body, adminHeaders(pw)).then((r) => r.data);
export const adminGoogleLocalFeedHealth = (pw) =>
  apiClient.get("/admin/google-local-feed/health", adminHeaders(pw)).then((r) => r.data);

// ---- BabyLoveGrowth SEO sync (iter56) ----
export const adminBabyloveStatus = (pw) =>
  apiClient.get("/admin/babylove/status", adminHeaders(pw)).then((r) => r.data);
export const adminBabyloveSync = (pw, dryRun = false) =>
  apiClient.post("/admin/babylove/sync", { dry_run: !!dryRun }, adminHeaders(pw)).then((r) => r.data);

// ---- AI Category Blog Generator (iter65) ----
export const adminBlogGenStatus = (pw) =>
  apiClient.get("/admin/blog/category-gen/status", adminHeaders(pw)).then((r) => r.data);
export const adminBlogGenGenerate = (pw, { onlyMissing = false, category = null } = {}) =>
  apiClient.post(
    "/admin/blog/category-gen/generate",
    category ? { category } : { only_missing: !!onlyMissing },
    { ...adminHeaders(pw), timeout: 120000 }, // 2-min timeout — endpoint returns immediately (background task)
  ).then((r) => r.data);
export const adminBlogSetPublished = (pw, slug, published) =>
  apiClient.post(`/admin/blog/${encodeURIComponent(slug)}/publish`, { published: !!published }, adminHeaders(pw)).then((r) => r.data);


// ---- SanMar Bulk Data sync (iter57) ----
export const adminSanmarStatus = (pw) =>
  apiClient.get("/admin/sanmar/status", adminHeaders(pw)).then((r) => r.data);
export const adminSanmarGetMode = (pw) =>
  apiClient.get("/admin/sanmar/mode", adminHeaders(pw)).then((r) => r.data);
export const adminSanmarSetMode = (pw, mode) =>
  apiClient.post("/admin/sanmar/mode", { mode }, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarSync = (pw, { mode = "sandbox", dryRun = false } = {}) =>
  apiClient.post("/admin/sanmar/sync", { mode, dry_run: !!dryRun }, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarStyles = (pw, { brand, approved, q, limit = 200 } = {}) => {
  const params = { limit };
  if (brand) params.brand = brand;
  if (typeof approved === "boolean") params.approved = approved;
  if (q) params.q = q;
  return apiClient.get("/admin/sanmar/styles", { ...adminHeaders(pw), params }).then((r) => r.data);
};
export const adminSanmarSetApproval = (pw, style, approved) =>
  apiClient.put(`/admin/sanmar/styles/${encodeURIComponent(style)}/approval`, { approved: !!approved }, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarRuns = (pw, limit = 20) =>
  apiClient.get("/admin/sanmar/runs", { ...adminHeaders(pw), params: { limit } }).then((r) => r.data);

// iter88 — one-click purge of failed + zombie SanMar sync log rows.
export const adminSanmarClearErrors = (pw) =>
  apiClient.post("/admin/sanmar/runs/clear-errors", {}, adminHeaders(pw)).then((r) => r.data);

// ---- SanMar Phase 2: promote to live catalog (iter58) ----
export const adminSanmarGetSettings = (pw) =>
  apiClient.get("/admin/sanmar/settings", adminHeaders(pw)).then((r) => r.data);
export const adminSanmarPutSettings = (pw, body) =>
  apiClient.put("/admin/sanmar/settings", body, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarPreview = (pw, style) =>
  apiClient.get(`/admin/sanmar/preview/${encodeURIComponent(style)}`, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarPromote = (pw, style, forceOverwrite = false, opts = {}) =>
  apiClient.post("/admin/sanmar/promote", {
    style, force_overwrite: !!forceOverwrite,
    category: opts.category || undefined,
    subcategory: opts.subcategory || undefined,
  }, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarPromoteAll = (pw, forceOverwrite = false, opts = {}) =>
  apiClient.post("/admin/sanmar/promote-all", {
    force_overwrite: !!forceOverwrite,
    category: opts.category || undefined,
    subcategory: opts.subcategory || undefined,
  }, adminHeaders(pw)).then((r) => r.data);

export const adminSanmarScrapeBacks = (pw, body = {}) =>
  apiClient.post("/admin/sanmar/scrape-backs", body, adminHeaders(pw)).then((r) => r.data);
// iter76 — spec-sheet scraper (SanMar PDF)
export const adminSanmarScrapeSpecs = (pw, body = {}) =>
  apiClient.post("/admin/sanmar/scrape-specs", body, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarScrapeSpecsStatus = (pw) =>
  apiClient.get("/admin/sanmar/scrape-specs/status", adminHeaders(pw)).then((r) => r.data);
export const adminSanmarScrapeStatus = (pw) =>
  apiClient.get("/admin/sanmar/scrape-status", adminHeaders(pw)).then((r) => r.data);
export const adminSanmarScrapeRuns = (pw, limit = 10) =>
  apiClient.get("/admin/sanmar/scrape-runs", { ...adminHeaders(pw), params: { limit } }).then((r) => r.data);
export const adminSanmarSyncLiveListings = (pw, body = {}) =>
  apiClient.post("/admin/sanmar/sync-live-listings", body, adminHeaders(pw)).then((r) => r.data);
export const adminSanmarSyncLiveStatus = (pw) =>
  apiClient.get("/admin/sanmar/sync-live-status", adminHeaders(pw)).then((r) => r.data);

// iter66 — category taxonomy + classifier + normalize
export const adminCategoriesTaxonomy = (pw) =>
  apiClient.get("/admin/categories/taxonomy", adminHeaders(pw)).then((r) => r.data);
export const adminCategoriesClassify = (pw, body) =>
  apiClient.post("/admin/categories/classify", body, adminHeaders(pw)).then((r) => r.data);
export const adminCategoriesNormalize = (pw, dry_run = false) =>
  apiClient.post("/admin/categories/normalize", { dry_run }, adminHeaders(pw)).then((r) => r.data);

// iter63 — Phase 3 health/verification endpoints
export const adminHealthFeedsRun = (pw) =>
  apiClient.post("/admin/health/feeds/run", {}, adminHeaders(pw)).then((r) => r.data);
export const adminHealthFeedsStatus = (pw) =>
  apiClient.get("/admin/health/feeds/status", adminHeaders(pw)).then((r) => r.data);
export const adminHealthPixels = (pw) =>
  apiClient.get("/admin/health/pixels", adminHeaders(pw)).then((r) => r.data);
export const adminHealthPixelTest = (pw, body = {}) =>
  apiClient.post("/admin/health/pixels/test-event", body, adminHeaders(pw)).then((r) => r.data);
export const adminHealthAuditRun = (pw) =>
  apiClient.post("/admin/health/audit/run", {}, adminHeaders(pw)).then((r) => r.data);
export const adminHealthAuditStatus = (pw) =>
  apiClient.get("/admin/health/audit/status", adminHeaders(pw)).then((r) => r.data);
export const adminHealthAuditDismiss = (pw, { product_id, code, reason }) =>
  apiClient.post("/admin/health/audit/dismiss",
    { product_id, code, reason: reason || null },
    adminHeaders(pw)).then((r) => r.data);
export const adminHealthAuditUndismiss = (pw, { product_id, code }) =>
  apiClient.delete("/admin/health/audit/dismiss",
    { ...adminHeaders(pw), params: { product_id, code } }).then((r) => r.data);
export const adminHealthAuditDismissals = (pw) =>
  apiClient.get("/admin/health/audit/dismissals", adminHeaders(pw)).then((r) => r.data);
export const adminHealthTimeline = (pw, limit = 20) =>
  apiClient.get("/admin/health/timeline", { ...adminHeaders(pw), params: { limit } }).then((r) => r.data);


// ===== PRODUCT REVIEWS (Iter 33) =====
export const submitReview = (body) =>
  apiClient.post("/reviews", body).then((r) => r.data);
export const listReviews = (product_id, product_type = "ready_made", limit = 50) =>
  apiClient.get("/reviews", { params: { product_id, product_type, limit } }).then((r) => r.data);
export const reviewsSummary = (product_id, product_type = "ready_made") =>
  apiClient.get("/reviews/summary", { params: { product_id, product_type } }).then((r) => r.data);

export const adminListReviews = (pw, status) =>
  apiClient.get("/admin/reviews", { ...adminHeaders(pw), params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateReview = (rid, body, pw) =>
  apiClient.patch(`/admin/reviews/${rid}`, body, adminHeaders(pw)).then((r) => r.data);

// ===== Review moderation settings (iter47d) =====
export const adminGetReviewSettings = (pw) =>
  apiClient.get("/admin/review-settings", adminHeaders(pw)).then((r) => r.data);
export const adminUpdateReviewSettings = (body, pw) =>
  apiClient.patch("/admin/review-settings", body, adminHeaders(pw)).then((r) => r.data);
export const adminApproveAllPendingReviews = (pw) =>
  apiClient.post("/admin/reviews-approve-all", {}, adminHeaders(pw)).then((r) => r.data);

// ===== DESIGNER 2.0 — Gallery, Remix, AI (Iter 34) =====
export const publishMyDesign = (did, body) =>
  apiClient.patch(`/me/designs/${did}/publish`, body).then((r) => r.data);

export const galleryList = (params = {}) =>
  apiClient.get("/designs/gallery", { params }).then((r) => r.data);
export const galleryGet = (did) =>
  apiClient.get(`/designs/gallery/${did}`).then((r) => r.data);
export const remixDesign = (did, name) =>
  apiClient.post(`/designs/gallery/${did}/remix`, { name }).then((r) => r.data);
export const likeGalleryDesign = (did) =>
  apiClient.post(`/designs/gallery/${did}/like`).then((r) => r.data);

export const adminDesignQueue = (pw, status) =>
  apiClient.get("/admin/designs/queue", { ...adminHeaders(pw), params: status ? { status } : {} }).then((r) => r.data);
export const adminModerateDesign = (did, action, pw) =>
  apiClient.patch(`/admin/designs/${did}/moderate`, { action }, adminHeaders(pw)).then((r) => r.data);

export const aiUsage = () =>
  apiClient.get("/ai/usage").then((r) => r.data);
// AI image generation can take 20–45s for Gemini Nano Banana on busy days.
// Default axios timeout is too short — bump these explicitly.
export const aiGenerateImage = (prompt) =>
  apiClient.post("/ai/generate-image", { prompt }, { timeout: 90000 }).then((r) => r.data);
export const aiEditImage = (image, prompt) =>
  apiClient.post("/ai/edit-image", { image, prompt }, { timeout: 90000 }).then((r) => r.data);
export const aiSlogans = (topic, n = 5) =>
  apiClient.post("/ai/slogans", { topic, n }, { timeout: 45000 }).then((r) => r.data);

// ===== INVOICES (Iter 35) =====

// AI budget status — used by the admin dashboard widget to surface
// EMERGENT_LLM_KEY budget health and a one-click "Add Balance" deep link.
export const adminAiBudgetStatus = (pw) =>
  apiClient.get("/ai/admin/budget-status", adminHeaders(pw)).then((r) => r.data);

// Admin
export const adminListInvoices = (pw, params = {}) =>
  apiClient.get("/admin/invoices", { ...adminHeaders(pw), params }).then((r) => r.data);
export const adminGetInvoice = (iid, pw) =>
  apiClient.get(`/admin/invoices/${iid}`, adminHeaders(pw)).then((r) => r.data);
export const adminCreateInvoice = (body, pw) =>
  apiClient.post("/admin/invoices", body, adminHeaders(pw)).then((r) => r.data);
export const adminUpdateInvoice = (iid, body, pw) =>
  apiClient.patch(`/admin/invoices/${iid}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteInvoice = (iid, pw) =>
  apiClient.delete(`/admin/invoices/${iid}`, adminHeaders(pw)).then((r) => r.data);
export const adminSendInvoice = (iid, pw) =>
  apiClient.post(`/admin/invoices/${iid}/send`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminDuplicateInvoice = (iid, pw) =>
  apiClient.post(`/admin/invoices/${iid}/duplicate`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminCancelInvoice = (iid, pw) =>
  apiClient.post(`/admin/invoices/${iid}/cancel`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminMarkInvoicePaid = (iid, body, pw) =>
  apiClient.post(`/admin/invoices/${iid}/mark-paid`, body, adminHeaders(pw)).then((r) => r.data);
export const adminAddInvoiceFile = (iid, body, pw) =>
  apiClient.post(`/admin/invoices/${iid}/files`, body, adminHeaders(pw)).then((r) => r.data);
export const adminDeleteInvoiceFile = (iid, fid, pw) =>
  apiClient.delete(`/admin/invoices/${iid}/files/${fid}`, adminHeaders(pw)).then((r) => r.data);
export const adminListJobs = (pw, status) =>
  apiClient.get("/admin/jobs", { ...adminHeaders(pw), params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateJob = (jid, body, pw) =>
  apiClient.patch(`/admin/jobs/${jid}`, body, adminHeaders(pw)).then((r) => r.data);
export const adminInvoicePdfUrl = (iid, pw) =>
  `${apiClient.defaults.baseURL}/admin/invoices/${iid}/pdf?_pw=${encodeURIComponent(pw)}`;

// Public
export const publicGetInvoice = (token) =>
  apiClient.get(`/invoices/${token}`).then((r) => r.data);
export const publicApproveInvoice = (token, body) =>
  apiClient.post(`/invoices/${token}/approve`, body).then((r) => r.data);
export const publicCreatePaySession = (token, body) =>
  apiClient.post(`/invoices/${token}/pay-session`, body).then((r) => r.data);
export const publicPayInvoice = (token, body) =>
  apiClient.post(`/invoices/${token}/pay`, body).then((r) => r.data);
export const publicUploadInvoiceFile = (token, body) =>
  apiClient.post(`/invoices/${token}/files`, body).then((r) => r.data);
export const publicInvoicePdfUrl = (token) =>
  `${apiClient.defaults.baseURL}/invoices/${token}/pdf`;

export const adminInvoiceRefund = (iid, body, pw) =>
  apiClient.post(`/admin/invoices/${iid}/refund`, body, adminHeaders(pw)).then((r) => r.data);
export const adminInvoiceResend = (iid, pw) =>
  apiClient.post(`/admin/invoices/${iid}/resend`, {}, adminHeaders(pw)).then((r) => r.data);
export const adminGetInvoiceSettings = (pw) =>
  apiClient.get("/admin/invoice-settings", adminHeaders(pw)).then((r) => r.data);
export const adminPatchInvoiceSettings = (body, pw) =>
  apiClient.patch("/admin/invoice-settings", body, adminHeaders(pw)).then((r) => r.data);


export const adminDeleteReview = (rid, pw) =>
  apiClient.delete(`/admin/reviews/${rid}`, adminHeaders(pw)).then((r) => r.data);


// ---- Order Tracking (Iter 37) ----
export const publicTrackOrderShipping = (orderNumber, email) =>
  apiClient
    .get(`/orders/${encodeURIComponent(orderNumber)}/tracking`, { params: { email } })
    .then((r) => r.data);

export const adminRegisterTracker = (orderId, body, pw) =>
  apiClient
    .post(`/admin/orders/${orderId}/register-tracker`, body, adminHeaders(pw))
    .then((r) => r.data);

export const adminRefreshTracker = (orderId, pw) =>
  apiClient
    .post(`/admin/orders/${orderId}/refresh-tracker`, {}, adminHeaders(pw))
    .then((r) => r.data);

export const adminGetOrderTracking = (orderId, pw) =>
  apiClient
    .get(`/admin/orders/${orderId}/tracking`, adminHeaders(pw))
    .then((r) => r.data);

export const listTrackingCarriers = () =>
  apiClient.get("/tracking/carriers").then((r) => r.data);


// ---- Customer Invoice Portal (account) ----
// The portal supports two auth modes:
//   1. Logged-in user — interceptor attaches `tc_auth_token`.
//   2. Magic-link guest — caller supplies a portal token via `portalToken`.
//      We override the Authorization header for that single call.
const portalHeaders = (portalToken) =>
  portalToken ? { headers: { Authorization: `Bearer ${portalToken}` } } : {};

export const requestInvoicePortalLink = (email) =>
  apiClient.post("/account/magic-link", { email }).then((r) => r.data);
export const verifyInvoicePortalToken = (token) =>
  apiClient.post("/account/magic-verify", { token }).then((r) => r.data);
export const listMyInvoices = (portalToken) =>
  apiClient.get("/account/invoices", portalHeaders(portalToken)).then((r) => r.data);
export const getMyInvoice = (iid, portalToken) =>
  apiClient.get(`/account/invoices/${iid}`, portalHeaders(portalToken)).then((r) => r.data);


// ---- Reorder flows (Iter 38) ----
export const adminCreateReorderInvoice = (orderId, pw) =>
  apiClient
    .post(`/admin/orders/${orderId}/create-reorder-invoice`, {}, adminHeaders(pw))
    .then((r) => r.data);

export const listMyOrders = (portalToken) =>
  apiClient.get("/account/orders", portalHeaders(portalToken)).then((r) => r.data);

export const getReorderItems = (orderId, portalToken) =>
  apiClient
    .get(`/account/orders/${orderId}/reorder-items`, portalHeaders(portalToken))
    .then((r) => r.data);

// Bulk-import reviews (used to re-seed prod from a preview JSON export)
export const adminImportReviewsBulk = (reviews, pw) =>
  apiClient
    .post("/admin/reviews/import-bulk", { reviews }, adminHeaders(pw))
    .then((r) => r.data);

// Export all reviews — used for "preview → prod" seeding workflow.
// Uses a programmatic download since the endpoint requires an admin header.
export const adminExportReviews = async (pw) => {
  const res = await apiClient.get("/admin/reviews/export", {
    ...adminHeaders(pw),
    responseType: "blob",
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "reviews_export.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// iter86 — Image overrides admin
export const adminListImageOverrides = (pw) =>
  apiClient.get("/admin/image-overrides", adminHeaders(pw)).then((r) => r.data);

export const adminCreateImageOverride = (pw, { slug, color, side, imageUrl, imageFile }) => {
  const fd = new FormData();
  fd.append("slug", slug);
  fd.append("color", color);
  fd.append("side", side);
  if (imageFile) fd.append("image_file", imageFile);
  if (imageUrl) fd.append("image_url", imageUrl);
  return apiClient
    .post("/admin/image-overrides", fd, { headers: { "X-Admin-Password": pw } })
    .then((r) => r.data);
};

export const adminDeleteImageOverride = (pw, ovId) =>
  apiClient.delete(`/admin/image-overrides/${ovId}`, adminHeaders(pw)).then((r) => r.data);

export const adminReapplyImageOverrides = (pw) =>
  apiClient.post("/admin/image-overrides/reapply", {}, adminHeaders(pw)).then((r) => r.data);

// Product lookup by slug — used to autocomplete the colour list in the
// Overrides form. This hits the public product endpoint, no auth needed.
export const publicGetProduct = (slug) =>
  apiClient.get(`/products/${slug}`).then((r) => r.data);


// iter92 — client-error reporting log (populated by lib/errorReporter.js)
export const adminListClientErrors = (pw, limit = 100) =>
  apiClient.get("/admin/client-errors", { ...adminHeaders(pw), params: { limit } }).then((r) => r.data);
export const adminClearClientErrors = (pw) =>
  apiClient.post("/admin/client-errors/clear", {}, adminHeaders(pw)).then((r) => r.data);

